import { useEffect, useRef } from 'react';
import type { Editor } from '@tiptap/react';
import { selectedRect, moveTableRow, moveTableColumn } from '@tiptap/pm/tables';

interface MenuAction {
  label: string;
  run: (chain: any, editor: Editor) => any;
  can?: (editor: Editor) => boolean;
  divider?: false;
}
interface MenuDivider {
  divider: true;
  label?: never;
  run?: never;
  can?: never;
}
type MenuItem = MenuAction | MenuDivider;

export function TableMenu({
  editor,
  onClose,
  anchor,
}: {
  editor: Editor;
  onClose: () => void;
  /** Trigger button's viewport rect — menu's right edge anchors to the
   * trigger's right edge. Position-fixed (not absolute) so the toolbar's
   * `overflow-x-auto` doesn't clip the menu. */
  anchor: DOMRect;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Element | null;
      if (!target) return;
      if (ref.current && ref.current.contains(target)) return;
      if (target.closest?.('[data-popover-trigger]')) return;
      onClose();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const inTable = editor.isActive('table');

  // Pulled from TipTap's table extension. `can()` lets us grey out items
  // that don't apply to the current selection (e.g. merge cells when not
  // in a table).
  const items: MenuItem[] = [
    {
      label: 'Insert 3×3 table',
      run: (c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }),
      can: (e) => e.can().insertTable({ rows: 3, cols: 3, withHeaderRow: true }),
    },
    { divider: true },
    {
      label: 'Add row above',
      run: (c) => c.addRowBefore(),
      can: (e) => inTable && e.can().addRowBefore(),
    },
    {
      label: 'Add row below',
      run: (c) => c.addRowAfter(),
      can: (e) => inTable && e.can().addRowAfter(),
    },
    {
      label: 'Add column left',
      run: (c) => c.addColumnBefore(),
      can: (e) => inTable && e.can().addColumnBefore(),
    },
    {
      label: 'Add column right',
      run: (c) => c.addColumnAfter(),
      can: (e) => inTable && e.can().addColumnAfter(),
    },
    { divider: true },
    {
      label: 'Toggle header row',
      run: (c) => c.toggleHeaderRow(),
      can: (e) => inTable && e.can().toggleHeaderRow(),
    },
    {
      label: 'Merge cells',
      run: (c) => c.mergeCells(),
      can: (e) => inTable && e.can().mergeCells(),
    },
    {
      label: 'Split cell',
      run: (c) => c.splitCell(),
      can: (e) => inTable && e.can().splitCell(),
    },
    { divider: true },
    {
      label: 'Move row up',
      run: (c, e) => {
        const r = selectedRect(e.state);
        if (r) {
          const cmd = moveTableRow({ from: r.top, to: Math.max(0, r.top - 1) });
          cmd(e.state, e.view.dispatch);
          e.view.focus();
        }
        return c;
      },
      can: (e) => {
        if (!inTable) return false;
        try {
          const r = selectedRect(e.state);
          return !!r && r.top > 0;
        } catch {
          return false;
        }
      },
    },
    {
      label: 'Move row down',
      run: (c, e) => {
        const r = selectedRect(e.state);
        if (r) {
          const cmd = moveTableRow({
            from: r.top,
            to: Math.min(r.map.height - 1, r.top + 1),
          });
          cmd(e.state, e.view.dispatch);
          e.view.focus();
        }
        return c;
      },
      can: (e) => {
        if (!inTable) return false;
        try {
          const r = selectedRect(e.state);
          return !!r && r.top < r.map.height - 1;
        } catch {
          return false;
        }
      },
    },
    {
      label: 'Move column left',
      run: (c, e) => {
        const r = selectedRect(e.state);
        if (r) {
          const cmd = moveTableColumn({
            from: r.left,
            to: Math.max(0, r.left - 1),
          });
          cmd(e.state, e.view.dispatch);
          e.view.focus();
        }
        return c;
      },
      can: (e) => {
        if (!inTable) return false;
        try {
          const r = selectedRect(e.state);
          return !!r && r.left > 0;
        } catch {
          return false;
        }
      },
    },
    {
      label: 'Move column right',
      run: (c, e) => {
        const r = selectedRect(e.state);
        if (r) {
          const cmd = moveTableColumn({
            from: r.left,
            to: Math.min(r.map.width - 1, r.left + 1),
          });
          cmd(e.state, e.view.dispatch);
          e.view.focus();
        }
        return c;
      },
      can: (e) => {
        if (!inTable) return false;
        try {
          const r = selectedRect(e.state);
          return !!r && r.left < r.map.width - 1;
        } catch {
          return false;
        }
      },
    },
    { divider: true },
    {
      label: 'Delete row',
      run: (c) => c.deleteRow(),
      can: (e) => inTable && e.can().deleteRow(),
    },
    {
      label: 'Delete column',
      run: (c) => c.deleteColumn(),
      can: (e) => inTable && e.can().deleteColumn(),
    },
    {
      label: 'Delete table',
      run: (c) => c.deleteTable(),
      can: (e) => inTable && e.can().deleteTable(),
    },
  ];

  function fire(item: MenuAction) {
    const result = item.run(editor.chain().focus(), editor);
    if (result && typeof result.run === 'function') result.run();
    onClose();
  }

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        top: anchor.bottom + 4,
        right: Math.max(8, window.innerWidth - anchor.right),
        zIndex: 50,
      }}
      className="w-48 rounded-md border border-ink-700 bg-ink-900 py-1 shadow-lg"
    >
      {items.map((item, i) =>
        item.divider ? (
          <div key={i} className="my-1 h-px bg-ink-800" />
        ) : (
          <button
            type="button"
            key={i}
            disabled={item.can ? !item.can(editor) : false}
            onClick={() => fire(item)}
            className="w-full text-left px-3 py-1 text-xs text-ink-200 hover:bg-ink-800 disabled:text-ink-600 disabled:hover:bg-transparent disabled:cursor-not-allowed"
          >
            {item.label}
          </button>
        ),
      )}
    </div>
  );
}
